import { create } from "zustand";
import { TutorEducation } from "src/interfaces/TutorProfile";
import { normalizeTutorEducation } from "src/utils/tutorEducation";

// Store for managing editing state of tutor profile sections
interface TutorProfileEditStore {
  // Editing state for different sections
  editingAchievements: string[];
  editingExpertise: string[];
  editingSubjects: string[];
  editingEducation: TutorEducation[];

  // Actions for achievements
  setEditingAchievements: (achievements: string[] | undefined) => void;
  addAchievement: () => void;
  removeAchievement: (index: number) => void;
  updateAchievement: (index: number, value: string) => void;

  // Actions for expertise
  setEditingExpertise: (expertise: string[] | undefined) => void;
  addExpertise: () => void;
  removeExpertise: (index: number) => void;
  updateExpertise: (index: number, value: string) => void;

  // Actions for subjects
  setEditingSubjects: (subjects: string[] | undefined) => void;
  addSubject: () => void;
  removeSubject: (index: number) => void;
  updateSubject: (index: number, value: string) => void;

  // Actions for education
  setEditingEducation: (education: TutorEducation[] | undefined) => void;
  addEducation: () => void;
  removeEducation: (index: number) => void;
  updateEducation: (index: number, field: keyof TutorEducation, value: any) => void;

  // Reset functions
  resetEditingState: () => void;
  resetSection: (section: 'achievements' | 'expertise' | 'subjects' | 'education') => void;
}

export const useTutorProfileEditStore = create<TutorProfileEditStore>((set, get) => ({
  // Initial state
  editingAchievements: [],
  editingExpertise: [],
  editingSubjects: [],
  editingEducation: [],

  // Achievements actions
  setEditingAchievements: (achievements) => set({ editingAchievements: achievements || [] }),
  
  addAchievement: () => set((state) => ({
    editingAchievements: [...state.editingAchievements, ""]
  })),
  
  removeAchievement: (index) => set((state) => ({
    editingAchievements: state.editingAchievements.filter((_, i) => i !== index)
  })),
  
  updateAchievement: (index, value) => set((state) => {
    const newAchievements = [...state.editingAchievements];
    newAchievements[index] = value;
    return { editingAchievements: newAchievements };
  }),

  // Expertise actions
  setEditingExpertise: (expertise) => set({ editingExpertise: expertise || [] }),
  
  addExpertise: () => set((state) => ({
    editingExpertise: [...state.editingExpertise, ""]
  })),
  
  removeExpertise: (index) => set((state) => ({
    editingExpertise: state.editingExpertise.filter((_, i) => i !== index)
  })),
  
  updateExpertise: (index, value) => set((state) => {
    const newExpertise = [...state.editingExpertise];
    newExpertise[index] = value;
    return { editingExpertise: newExpertise };
  }),

  // Subjects actions
  setEditingSubjects: (subjects) => set({ editingSubjects: subjects || [] }),
  
  addSubject: () => set((state) => ({
    editingSubjects: [...state.editingSubjects, ""]
  })),
  
  removeSubject: (index) => set((state) => ({
    editingSubjects: state.editingSubjects.filter((_, i) => i !== index)
  })),
  
  updateSubject: (index, value) => set((state) => {
    const newSubjects = [...state.editingSubjects];
    newSubjects[index] = value;
    return { editingSubjects: newSubjects };
  }),

  // Education actions
  setEditingEducation: (education) => set({ editingEducation: normalizeTutorEducation(education) }),
  
  addEducation: () => set((state) => ({
    editingEducation: [...state.editingEducation, { 
      degree: "", 
      field: "", 
      institution: "", 
      country: "",
      year_of_completion: 0,
      gpa: undefined,
      is_custom_degree: false,
      gpa_total: undefined,
      level: ""
    }]
  })),
  
  removeEducation: (index) => set((state) => ({
    editingEducation: state.editingEducation.filter((_, i) => i !== index)
  })),
  
  updateEducation: (index, field, value) => set((state) => {
    const newEducation = [...state.editingEducation];
    newEducation[index] = { ...newEducation[index], [field]: value };
    return { editingEducation: newEducation };
  }),

  // Reset functions
  resetEditingState: () => set({
    editingAchievements: [],
    editingExpertise: [],
    editingSubjects: [],
    editingEducation: []
  }),

  resetSection: (section) => set((state) => {
    switch (section) {
      case 'achievements':
        return { editingAchievements: [] };
      case 'expertise':
        return { editingExpertise: [] };
      case 'subjects':
        return { editingSubjects: [] };
      case 'education':
        return { editingEducation: [] };
      default:
        return state;
    }
  })
}));
